"use client"

import { useEffect, useRef } from "react"

interface AutoplayVideoProps extends React.VideoHTMLAttributes<HTMLVideoElement> {
  /** Fraction of the video that must be visible to start playing. Default 0.25 */
  threshold?: number
}

/**
 * Drop-in replacement for <video autoPlay muted loop playsInline>.
 * Plays only while the element is in the viewport; pauses otherwise.
 */
export function AutoplayVideo({
  threshold = 0.25,
  className,
  children,
  ...props
}: AutoplayVideoProps) {
  const ref = useRef<HTMLVideoElement>(null)

  useEffect(() => {
    const video = ref.current
    if (!video) return

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          video.play().catch(() => {})
        } else {
          video.pause()
        }
      },
      { threshold }
    )

    observer.observe(video)
    return () => observer.disconnect()
  }, [threshold])

  // Remove autoPlay from the DOM — playback is driven by the observer
  return (
    <video
      ref={ref}
      muted
      loop
      playsInline
      {...props}
      autoPlay={false}
      className={className}
    >
      {children}
    </video>
  )
}
